import { PointerEvent as ReactPointerEvent, useCallback, useEffect, useRef, useState } from 'react';

type PanelSize = { height: number; width: number };

type Press = Pick<ReactPointerEvent, 'button' | 'clientX' | 'clientY' | 'pointerId' | 'preventDefault'>;

const MAIN_BUTTON = 0;

const pixels = (length: string, open: number) => {
  const value = parseFloat(length);
  return Number.isNaN(value) ? open : value;
};

const within = (value: number, min: string, max: string) => Math.min(pixels(max, Infinity), Math.max(pixels(min, 0), value));

export const usePanelSize = () => {
  const panelRef = useRef<HTMLElement>(null);
  const dragRef = useRef<AbortController>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [size, setSize] = useState<PanelSize>();

  useEffect(() => () => dragRef.current?.abort(), []);

  const toggleExpanded = useCallback(() => setIsExpanded(wasExpanded => !wasExpanded), []);

  const startResize = useCallback((event: Press) => {
    const panel = panelRef.current;
    if (!panel || event.button !== MAIN_BUTTON) {
      return;
    }
    event.preventDefault();
    dragRef.current?.abort();
    const { height, width } = panel.getBoundingClientRect();
    const { maxHeight, maxWidth, minHeight, minWidth } = getComputedStyle(panel);
    const { clientX: startX, clientY: startY, pointerId } = event;
    const drag = new AbortController();
    dragRef.current = drag;
    const endDrag = ({ pointerId: endedId }: PointerEvent) => {
      if (endedId === pointerId) {
        drag.abort();
      }
    };

    window.addEventListener(
      'pointermove',
      move => {
        if (move.pointerId !== pointerId) {
          return;
        }
        if (move.buttons === 0) {
          drag.abort();
          return;
        }
        setSize({
          height: within(height + startY - move.clientY, minHeight, maxHeight),
          width: within(width + move.clientX - startX, minWidth, maxWidth),
        });
      },
      { signal: drag.signal },
    );
    window.addEventListener('pointerup', endDrag, { signal: drag.signal });
    window.addEventListener('pointercancel', endDrag, { signal: drag.signal });
  }, []);

  return { isExpanded, panelRef, size, startResize, toggleExpanded };
};
